import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { RelatedType, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import {
  EscalationReason,
  ThreadAIOutcome,
  ThreadReadStatus,
  TriggerType,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { FlatIndexLocationWithAlign, ListRange, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { CommPreferenceV1 } from '@frontend/api-comm-preference';
import { MediaQueries } from '@frontend/api-media';
import { SMSDataSharedUtils, SMSDataV3 } from '@frontend/api-sms-data';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, SpinningLoader, Text, useDebouncedValue } from '@frontend/design-system';
import { useGetThreadData } from '../hooks/use-get-thread-data';
import { SMSContextActions } from '../types';
import { ThreadDateTimeDivider, BackgroundHighlight, SMSThreadItem } from './thread-items';
import { AiThreadItem } from './thread-items/ai-thread-item';
import { VoiceThreadItem } from './thread-items/voice-thread-item';

const START_INDEX = 100000;
const NEW_WRITEBACK_UI_DELAY = 30000; // 30 seconds

export const DEFAULT_ENABLE_SMS_SELECTION_PREDICATE = (sms: SMS): boolean => {
  const hasError =
    (sms.status === Status.STATUS_NOT_SENT &&
      sms.statusDetails !== SMSDataV3.Types.OptimisticUpdateStatusDetails.SENDING) ||
    sms.status === Status.STATUS_ERROR;
  return !hasError;
};

type ScrollState = 'atBottom' | 'atTop' | 'inMiddle';
type VirtuosoContext = {
  hasOlderMessages: boolean;
  hasNewerMessages: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isNewConversation: boolean;
  optOutBannerHeight?: number;
  mediaQueries: ReturnType<typeof MediaQueries.useMmsMedia>;
  targetSmsId?: string;
  firstUnreadMessageId?: string;
  isSelecting?: boolean;
  selectedSmsIds?: string[];
  allIsSelected?: boolean;
  onToggleSmsSelection?: (smsId: string) => void;
  hiddenContextActions?: SMSContextActions[];
  hideWritebacks?: boolean;
  smsIdsWithNewWritebacks: string[];
  enableSmsSelectionPredicate: (sms: SMS) => boolean;
};

type ThreadListProps = {
  groupId: string;
  threadId: string;
  targetSmsData?: {
    id: string;
    createdAt: string;
    click?: number;
  };
  personPhone: string;
  onNewMessage?: () => void;
  unreadCount?: number;
  uiType: 'mini-chat' | 'inbox' | 'integrated-messaging';
  isSelecting?: boolean;
  selectedSmsIds?: string[];
  allIsSelected?: boolean;
  onToggleSmsSelection?: (smsId: string) => void;
  hiddenContextActions?: SMSContextActions[];
  hideWritebacks?: boolean;
  enableSmsSelectionPredicate?: (sms: SMS, defaultPredicate: (sms: SMS) => boolean) => boolean | undefined;
  mediaMaxWidth?: ComponentProps<typeof SMSThreadItem>['mediaMaxWidth'];
  trackingIdPrefix?: string;
};

export const ThreadList = ({
  groupId,
  threadId,
  targetSmsData,
  personPhone: providedPersonPhone,
  onNewMessage,
  unreadCount,
  uiType,
  isSelecting,
  selectedSmsIds,
  allIsSelected,
  onToggleSmsSelection,
  hiddenContextActions,
  hideWritebacks,
  enableSmsSelectionPredicate: providedSmsSelectionPredicate,
  mediaMaxWidth,
  trackingIdPrefix,
  ...rest
}: ThreadListProps) => {
  const { t } = useTranslation('thread-body');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const inboxFusionEnabled = getFeatureFlagValue('nwx:inbox-fusion');
  const [showBlockedThread, setShowBlockedThread] = useState(false);
  const enableSmsSelectionPredicate = useCallback(
    (sms: SMS): boolean => {
      return (
        providedSmsSelectionPredicate?.(sms, DEFAULT_ENABLE_SMS_SELECTION_PREDICATE) ??
        DEFAULT_ENABLE_SMS_SELECTION_PREDICATE(sms)
      );
    },
    [providedSmsSelectionPredicate]
  );

  const user = getUser();

  const { isSmsMessageItem, isCallRecordItem, isAiActivityItem, getThreadItemId, getThreadPersonPhone } =
    SMSDataSharedUtils.threadItemsUtils;

  const {
    getThreadQuery,
    threadItems,
    latestSmsItem,
    firstItemId,
    firstUnreadMessageId,
    threadItemsDateList,
    contact,
  } = useGetThreadData({
    threadId,
    locationId: groupId,
    focusItemId: targetSmsData?.id,
    focusItemCreatedAt: targetSmsData?.createdAt,
    unreadCount: unreadCount,
  });

  const personPhone = formatPhoneNumberE164(getThreadPersonPhone(threadItems[0]) || providedPersonPhone);
  const { data: isOptedOut } = CommPreferenceV1.Queries.useCheckPreferenceQuery({
    request: {
      userChannelAddress: personPhone,
      channel: Channel.CHANNEL_SMS,
      locationId: groupId,
      messageType: MessageType_Enum.MESSAGING_MANUAL,
    },
    options: {
      enabled: !!personPhone && !!groupId,
      select: (data) => data.consented === false,
    },
  });

  const latestMessageAutogeneratedBy = latestSmsItem?.smsMessage.autogeneratedBy;
  const isAutomated = useMemo(() => {
    if (!getThreadQuery.hasPreviousPage) {
      return undefined;
    }

    return latestMessageAutogeneratedBy === undefined ? undefined : !!latestMessageAutogeneratedBy;
  }, [getThreadQuery.hasPreviousPage, latestMessageAutogeneratedBy]);
  const markAsReadMutation = SMSDataV3.Mutations.useSetThreadsStatusReadMutation();
  const isUnread = getThreadQuery.data?.pages.some((page) =>
    ['new', ThreadReadStatus.THREAD_READ_STATUS_NEW].includes(page.thread.status)
  );
  const markAsRead = () => {
    if (!markAsReadMutation.isLoading && isUnread && !!user?.userID) {
      markAsReadMutation.mutate({
        threadIds: [threadId],
        locationId: groupId,
        groupIds: [groupId],
        userId: user.userID,
        _otherOptions: {
          automatedThreadIds: isAutomated ? [threadId] : [],
          manualThreadIds: isAutomated ? [] : [threadId],
        },
      });
    }
  };

  const [debouncedUnreadMessageId, setDebouncedUnreadMessageId] = useState(firstUnreadMessageId);

  const smsIdsWithWritebacks = useMemo(
    () =>
      hideWritebacks
        ? []
        : threadItems.reduce<string[]>((acc, item) => {
            if (
              isSmsMessageItem(item) &&
              item.smsMessage.relatedIds.some(
                (relatedId) => relatedId.type === RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE
              )
            ) {
              acc.push(item.smsMessage.id);
            }
            return acc;
          }, []),
    [threadItems, hideWritebacks]
  );
  const debouncedSmsIdsWithWritebacks = useDebouncedValue(smsIdsWithWritebacks, NEW_WRITEBACK_UI_DELAY);
  const smsIdsWithNewWritebacks = useMemo(
    () => smsIdsWithWritebacks.filter((smsId) => !debouncedSmsIdsWithWritebacks.includes(smsId)),
    [debouncedSmsIdsWithWritebacks, smsIdsWithWritebacks]
  );

  useEffect(() => {
    if (debouncedUnreadMessageId && !firstUnreadMessageId) {
      setTimeout(() => {
        setDebouncedUnreadMessageId(firstUnreadMessageId);
      }, 3000);
      return;
    }

    setDebouncedUnreadMessageId(firstUnreadMessageId);
  }, [firstUnreadMessageId]);

  const mediaIds = threadItems
    .filter(isSmsMessageItem)
    .flatMap((item) => item.smsMessage.media.map(({ mediaId }) => mediaId));
  const mediaQueries = MediaQueries.useMmsMedia({ mediaIds, locationId: groupId });
  const [scrollState, setScrollState] = useState<ScrollState>(targetSmsData ? 'inMiddle' : 'atBottom');

  const [virtuosoListData, setVirtuosoListData] = useState<{
    firstItemId: string;
    firstItemIndex: number;
    listLength: number;
  }>({
    firstItemId,
    firstItemIndex: START_INDEX,
    listLength: threadItems.length,
  });
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  const [directionToFetch, setDirectionToFetch] = useState<'previous' | 'next'>();
  const someMediaIsLoading = Object.values(mediaQueries).some((mediaQuery) => mediaQuery.isLoading);
  const ref = useRef<VirtuosoHandle>(null);

  // For callRecords, getThreadItemId return threadCallId, whereas targetSmsData stores the channelId.
  // and since callRecords can either be a voicemail or a call, we need to match on both channelId and startedAt.
  const targetSmsIndexResult = threadItemsDateList.findIndex(
    (item) =>
      typeof item !== 'string' &&
      (isCallRecordItem(item)
        ? item.callRecord.channelId === targetSmsData?.id && item.callRecord.startedAt === targetSmsData?.createdAt
        : getThreadItemId(item) === targetSmsData?.id)
  );

  // If the index search failed, fall back to 0 (first element) to avoid passing -1 to Virtuoso.
  // Returning 0 is safer than passing a negative index which causes react-virtuoso to throw.
  const targetSmsIndex = targetSmsIndexResult === -1 ? 0 : targetSmsIndexResult;

  const initialTopMostItemIndex: FlatIndexLocationWithAlign = {
    index: targetSmsData?.id ? targetSmsIndex : 'LAST',
    align: targetSmsData?.id ? 'center' : 'end',
    behavior: 'auto',
  };

  // Resolve the target id to the canonical threadItemId when possible so the
  // BackgroundHighlight (which compares context.targetSmsId === threadItemId)
  // will match for callRecord items (where the incoming id may be a channelId).
  const matchedItem =
    targetSmsIndexResult >= 0 && typeof threadItemsDateList[targetSmsIndexResult] !== 'string'
      ? threadItemsDateList[targetSmsIndexResult]
      : undefined;

  const resolvedTargetSmsId = matchedItem ? getThreadItemId(matchedItem) : targetSmsData?.id;

  const handlePageFetch = async () => {
    if (directionToFetch === 'previous') {
      await getThreadQuery.fetchPreviousPage();
    } else if (directionToFetch === 'next') {
      await getThreadQuery.fetchNextPage();
    }
  };

  const handleRangeChange = (range: ListRange) => {
    if (hasInitiallyScrolled) return;
    if (!targetSmsData) {
      // Wait for first page to finish rendering before setting initially scrolled to true
      setTimeout(() => {
        setHasInitiallyScrolled(true);
      }, 500);
    } else if (
      range.startIndex <= virtuosoListData.firstItemIndex + targetSmsIndex &&
      range.endIndex >= virtuosoListData.firstItemIndex + targetSmsIndex
    ) {
      // Wait for first page to finish rendering before setting initially scrolled to true
      setTimeout(() => {
        setHasInitiallyScrolled(true);
      }, 500);
    }
  };

  useEffect(() => {
    const firstItemHasChanged = virtuosoListData.firstItemId && firstItemId !== virtuosoListData.firstItemId;
    if (!firstItemHasChanged) {
      setVirtuosoListData((prev) => ({
        ...prev,
        firstItemId,
        listLength: threadItemsDateList.length,
      }));
    } else {
      const newItemsCount = threadItemsDateList.length - virtuosoListData.listLength;
      setVirtuosoListData((prev) => ({
        ...prev,
        listLength: threadItemsDateList.length,
        firstItemIndex: prev.firstItemIndex - newItemsCount,
      }));
    }
    // Cooldown so that multiple pages don't get fetched accidentally at the same time
    setTimeout(() => {
      setDirectionToFetch(undefined);
    }, 500);
  }, [threadItemsDateList.length]);

  useEffect(() => {
    setVirtuosoListData({
      firstItemId,
      firstItemIndex: START_INDEX,
      listLength: threadItemsDateList.length,
    });
    setDirectionToFetch(undefined);
  }, [threadId, targetSmsData?.id]);

  useEffect(() => {
    if (!hasInitiallyScrolled && !!getThreadQuery.data && !someMediaIsLoading) {
      // use timeout here to allow the messages to finish rendering before initial scroll
      setTimeout(() => {
        ref.current?.scrollToIndex(initialTopMostItemIndex);
      }, 300);
    }
  }, [someMediaIsLoading, !!getThreadQuery.data]);

  useEffect(() => {
    if (!someMediaIsLoading && !!getThreadQuery.data) handlePageFetch();
  }, [!!getThreadQuery.data, directionToFetch]);

  useEffect(() => {
    if (firstItemId) {
      onNewMessage?.();
    }
  }, [firstItemId]);

  useEffect(() => {
    setShowBlockedThread(false);
    setHasInitiallyScrolled(false);
  }, [threadId]);

  useEffect(() => {
    if (targetSmsData?.click ?? 0 > 1) {
      ref.current?.scrollToIndex({ ...initialTopMostItemIndex, behavior: 'smooth' });
    }
  }, [targetSmsData?.click]);

  useEffect(() => {
    const initialIndexIsLast = initialTopMostItemIndex.index === 'LAST' || initialTopMostItemIndex.index < 0;
    if (targetSmsData?.id && targetSmsData.createdAt && initialIndexIsLast) {
      return;
    }

    if (hasInitiallyScrolled) {
      return;
    }

    const timeout = setTimeout(() => {
      ref.current?.scrollToIndex({ ...initialTopMostItemIndex, behavior: targetSmsData?.id ? 'smooth' : 'auto' });
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [targetSmsData?.id, !!ref.current, initialTopMostItemIndex]);

  useEffect(() => {
    setHasInitiallyScrolled(false);
  }, [targetSmsData?.id, targetSmsData?.createdAt]);

  const prevLastAiActivityCreatedAt = useRef<string | undefined>();

  useEffect(() => {
    if (!inboxFusionEnabled) return;
    let timeout: NodeJS.Timeout;
    const lastItem = threadItems[threadItems.length - 1];
    const lastItemIsAiActivity = lastItem && isAiActivityItem(lastItem);
    if (!lastItemIsAiActivity) return;
    const lastItemCreatedAtChanged = lastItem?.aiActivity?.createdAt !== prevLastAiActivityCreatedAt.current;

    if (lastItemIsAiActivity && lastItemCreatedAtChanged) {
      timeout = setTimeout(() => {
        ref.current?.scrollToIndex({ index: 'LAST', align: 'start', behavior: 'auto' });
      }, 300);
      prevLastAiActivityCreatedAt.current = lastItem?.aiActivity?.createdAt;
    }

    prevLastAiActivityCreatedAt.current = lastItem?.aiActivity?.createdAt;

    return () => clearTimeout(timeout);
  }, [threadItems, isAiActivityItem, targetSmsData]);

  SMSDataSharedUtils.useReportTimeToOpenToPendo(
    threadItems.filter(isSmsMessageItem).at(-1)?.smsMessage,
    uiType,
    !!getThreadQuery.hasPreviousPage || getThreadQuery.isLoading
  );

  const debouncedHasPreviousPage = useDebouncedValue(getThreadQuery.hasPreviousPage, 500);

  const filteredThreadItemsDateList = inboxFusionEnabled
    ? threadItemsDateList.filter((threadItem) => {
        if (typeof threadItem === 'string') return true;
        if (!isAiActivityItem(threadItem)) return true;
        const aiItem = threadItem.aiActivity;
        // once BE schema is finalized, part will be moved to a util to check if ai activity should be shown
        return !(
          (aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME &&
            aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_UNSPECIFIED) ||
          aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_DEESCALATED ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_CANCEL ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_RESCHEDULE ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_BOOKING
        );
      })
    : threadItemsDateList;

  if (getThreadQuery.data?.pages[0]?.thread.isBlocked && !showBlockedThread) {
    return <BlockedMessageContent show={() => setShowBlockedThread(true)} />;
  }

  return (
    <Virtuoso<(typeof threadItemsDateList)[number], VirtuosoContext>
      key={`${threadId}-${targetSmsData?.id ?? ''}`}
      ref={ref}
      style={{
        height: 'auto',
        flexGrow: 1,
        opacity: isOptedOut ? 0.5 : 1,
      }}
      data-trackingid={`${trackingIdPrefix}-thread-list`}
      alignToBottom
      followOutput={(isAtBottom) => {
        if (targetSmsData?.id && targetSmsData.createdAt && !hasInitiallyScrolled) {
          return false;
        }
        if (isAtBottom && !debouncedHasPreviousPage) return 'smooth';
        return false;
      }}
      firstItemIndex={virtuosoListData.firstItemIndex}
      data={filteredThreadItemsDateList}
      startReached={() => {
        // Only fetch older pages after initial scroll has completed; prevents unwanted prefetch when focusing thread items
        if (hasInitiallyScrolled && getThreadQuery.hasNextPage) setDirectionToFetch('next');
      }}
      atBottomThreshold={25}
      atTopStateChange={(isAtTop) => {
        setScrollState(isAtTop ? 'atTop' : 'inMiddle');
        if (isAtTop && hasInitiallyScrolled) {
          setDirectionToFetch('next');
        }
      }}
      atBottomStateChange={(isAtBottom) => {
        setScrollState(isAtBottom ? 'atBottom' : 'inMiddle');
        if (isAtBottom && hasInitiallyScrolled) {
          setDirectionToFetch('previous');
          markAsRead();
        }
      }}
      onFocus={() => {
        if (scrollState === 'atBottom') {
          markAsRead();
        }
      }}
      // Layout shifts when selecting items make it necessary to increase the viewport to allow messages to completely
      // leave viewport before being unrendered
      increaseViewportBy={isSelecting ? 150 : 0}
      context={{
        hasOlderMessages: !!getThreadQuery.hasNextPage,
        hasNewerMessages: !!getThreadQuery.hasPreviousPage,
        isFetching: getThreadQuery.isFetching,
        isNewConversation: !getThreadQuery.isFetching && !threadItems.length,
        isLoading: (!getThreadQuery.data && !getThreadQuery.isError) || getThreadQuery.isLoading,
        mediaQueries,
        targetSmsId: resolvedTargetSmsId,
        firstUnreadMessageId: debouncedUnreadMessageId,
        isSelecting,
        selectedSmsIds,
        allIsSelected,
        onToggleSmsSelection,
        hiddenContextActions,
        smsIdsWithNewWritebacks,
        enableSmsSelectionPredicate,
      }}
      rangeChanged={handleRangeChange}
      components={{
        Header: ({ context }) => {
          if (context?.isLoading && !context?.isNewConversation) {
            return (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(1, 0),
                  width: '100%',
                }}
              >
                <SpinningLoader size='small' />
              </div>
            );
          }

          if (context?.hasOlderMessages && context?.isFetching && !context?.isLoading) {
            return (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(1, 0),
                  width: '100%',
                }}
              >
                <SpinningLoader size='small' />
              </div>
            );
          }
          if (!context?.isNewConversation && !context?.hasOlderMessages) {
            return (
              <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                🎉 {t('You have reached the end')}
              </Text>
            );
          }
          return null;
        },
        Footer: ({ context }) => {
          if (context?.isNewConversation)
            return (
              <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                {t('No messages yet.')}
              </Text>
            );

          if (context?.hasNewerMessages && context?.isFetching && !context?.isLoading)
            return (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(1, 0),
                  width: '100%',
                }}
              >
                <SpinningLoader size='small' />
              </div>
            );

          return null;
        },
      }}
      itemContent={(index, threadItem, context) => {
        if (typeof threadItem === 'string') {
          return <ThreadDateTimeDivider key={threadItem} timestamp={new Date(threadItem)} />;
        }

        const threadItemId = getThreadItemId(threadItem);
        const mediaIds = isSmsMessageItem(threadItem) ? threadItem.smsMessage.media.map(({ mediaId }) => mediaId) : [];
        const media = context.mediaQueries.reduce<NonNullable<(typeof context.mediaQueries)[number]['data']>[]>(
          (acc, { data }) => {
            if (!data || !mediaIds.includes(data.mediaId)) return acc;
            acc.push(data);
            return acc;
          },
          []
        );

        return (
          <>
            {context?.firstUnreadMessageId && threadItemId === context.firstUnreadMessageId && (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  width: '100%',
                  padding: theme.spacing(0, 1, 2),
                }}
              >
                <figure css={{ height: 1, backgroundColor: theme.colors.primary20, flexGrow: 1 }} />
                <Text size='small' color='primary' textAlign='center'>
                  {t('New')}
                </Text>
                <figure css={{ height: 1, backgroundColor: theme.colors.primary20, flexGrow: 1 }} />
              </div>
            )}
            <div
              css={{
                position: 'relative',
              }}
              data-trackingid={`${trackingIdPrefix}-thread-item-${threadItemId}`}
            >
              {context?.targetSmsId === threadItemId && (
                <BackgroundHighlight
                  additionalCSS={{
                    default: {
                      top: theme.spacing(-1),
                      bottom: theme.spacing(1),
                    },
                  }}
                  css={{
                    zIndex: theme.zIndex.background,
                  }}
                />
              )}
              {isSmsMessageItem(threadItem) && (
                <SMSThreadItem
                  key={threadItemId + index}
                  sms={threadItem.smsMessage}
                  avatarProps={{
                    size: 'small',
                  }}
                  media={media}
                  css={{
                    padding: theme.spacing(0, 2),
                  }}
                  contactName={{
                    name: getFullName(contact ?? {}),
                    firstName: contact?.firstName,
                    lastName: contact?.lastName,
                  }}
                  isSelecting={context.isSelecting}
                  isSelected={
                    context.enableSmsSelectionPredicate(threadItem.smsMessage)
                      ? context.allIsSelected || context.selectedSmsIds?.includes(threadItem.smsMessage.id)
                      : false
                  }
                  onToggleSmsSelection={() => context.onToggleSmsSelection?.(threadItem.smsMessage.id)}
                  hasNewWriteback={context.smsIdsWithNewWritebacks.includes(threadItem.smsMessage.id)}
                  selectionDisabled={
                    !context.enableSmsSelectionPredicate(threadItem.smsMessage) || !!threadItem.smsMessage.deletedAt
                  }
                  mediaMaxWidth={mediaMaxWidth}
                  hiddenContextActions={context.hiddenContextActions}
                  hideWritebacks={hideWritebacks}
                />
              )}
              {isCallRecordItem(threadItem) && (
                <VoiceThreadItem
                  voiceItem={threadItem.callRecord}
                  // Voicemails and mini-chat should have no context actions.
                  // TODO: add only tagging action to voicemails once BE supports it.
                  hiddenContextActions={
                    uiType === 'mini-chat' ? context.hiddenContextActions : ['delete', 'copy', 'tasks']
                  }
                  avatarProps={{
                    size: 'small',
                  }}
                  contact={contact}
                  threadId={threadId}
                />
              )}
              {isAiActivityItem(threadItem) && inboxFusionEnabled && <AiThreadItem aiItem={threadItem.aiActivity} />}
            </div>
          </>
        );
      }}
      initialTopMostItemIndex={initialTopMostItemIndex}
      {...rest}
    />
  );
};

const BlockedMessageContent = ({ show }: { show: () => void }) => {
  const { t } = useTranslation('messages');
  return (
    <section
      css={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Icon name='block' size={112} />
      <Text>{t('This sender was blocked by your organization')}</Text>
      <Button variant='tertiary' onClick={show}>
        {t('View messages anyway')}
      </Button>
    </section>
  );
};
